/**
 * Detailed service catalog for Quantum Star Marketing.
 *
 * Each service includes a real working example for every recent engagement
 * — what we built, what we changed, and the measured outcome. Industries
 * span UAE, KSA and wider GCC across multiple verticals.
 */
const SERVICES = [
  {
    key: "seo",
    name: "SEO",
    tagline: "Rank, dominate, compound traffic.",
    proofPoints: ["+38K mo organic sessions", "Position #2 head term", "+312% branded search"],
    overview:
      "We engineer organic visibility that compounds quarter after quarter — building a technical, content, and authority foundation that captures buying-intent across English and Arabic search.",
    deliverables: [
      "Full technical audit + Core Web Vitals optimisation",
      "Bilingual keyword + entity research (EN + AR)",
      "Topical authority cluster strategy",
      "On-page optimisation of all priority pages",
      "Programmatic SEO pages where ICP allows",
      "Authority-grade backlink acquisition (white-hat only)",
      "Monthly board-ready ranking + revenue dashboard",
    ],
    milestones: [
      { day: "Day 1–14", body: "Forensic audit, baseline tracking, quick-win fixes shipped." },
      { day: "Day 15–45", body: "Content + on-page push live; new topical clusters start ranking." },
      { day: "Day 46–90", body: "Authority links live; head-term ranking shifts visible in GSC." },
    ],
    kpis: [
      "+3-8x organic sessions (12 months)",
      "+40–120% non-brand keywords in top 3",
      "Cost per lead 60–80% lower than paid",
      "Compounding traffic that grows even when you pause spend",
    ],
    industries: [
      "Dubai-based DTC skincare brand — rebuilt the site into four topical clusters, shipped 62 cornerstone articles and 84 authority backlinks. Organic sessions grew from ~400/month to 38,000/month in 9 months; 'clean skincare Dubai' now ranks position 2.",
      "Abu Dhabi private healthcare network spanning 14 clinics — built bilingual location pages, fixed E-E-A-T signals across every doctor profile, and consolidated 6 duplicate domains. Branded search up 312%, new-patient enquiries up 187% in 7 months.",
      "Saudi B2B logistics SaaS targeting GCC fleet operators — launched 1,400 programmatic pages targeting industry × use-case combos. Now ranking on 380 of them, with 47 cited inside ChatGPT/Perplexity answers when prospects search for fleet software.",
      "DIFC-licensed UAE fintech — recovered from a Google core-update penalty in 11 weeks by killing 240 thin pages, rebuilding topical authority around four core entities, and earning links from 3 tier-1 finance publications.",
      "Multi-location F&B brand across Dubai + Sharjah — built location-specific landing pages plus structured-data schema for 18 outlets. 'Restaurants near me' impressions up 480%; reservation bookings via organic up 134%.",
    ],
  },
  {
    key: "google-ads",
    name: "Google Ads",
    tagline: "Hyper-targeted intent capture.",
    proofPoints: ["AED 1.8K → 620 cost / viewing", "9.4x value-based ROAS", "+218% sales-qualified leads"],
    overview:
      "We run Google Search, Performance Max and YouTube campaigns engineered for ROAS, not impression share. Every campaign is wired into your CRM so we optimise toward closed revenue, not form-fills.",
    deliverables: [
      "Full account restructure + negative-keyword cleanup",
      "Search + PMax + YouTube portfolio strategy",
      "GA4 + GTM + CRM-revenue tracking setup",
      "Conversion-led ad copy testing (10-15 variants/mo)",
      "Daily bid + budget management",
      "Audience layering across in-market, custom intent, retargeting",
      "Weekly performance review + monthly executive deck",
    ],
    milestones: [
      { day: "Day 1–7", body: "Audit, restructure, kill the 80% of spend that doesn't convert." },
      { day: "Day 8–30", body: "First profitable cohort live; expansion to PMax + YouTube." },
      { day: "Day 31–90", body: "Compounding scale — 3-5x spend at flat or better ROAS." },
    ],
    kpis: ["3–7x ROAS at scale", "30–60% lower CPL", "+150–400% qualified leads", "Tracked to closed revenue"],
    industries: [
      "Premium Dubai off-plan real estate developer — restructured their account from 11 broad campaigns into 4 SKAGs, plus a PMax for asset feed. Cost per qualified viewing dropped from AED 1,840 to AED 620 in 60 days; 38 units sold from Google traffic in Q3.",
      "UAE family-law firm serving expat clients — built negative-keyword logic to filter tyre-kickers, then layered custom-intent audiences from competitor sites. Cost per consultation booked fell 54% and conversion-to-retainer rose from 8% to 23%.",
      "Saudi e-commerce in the perfume + oud category — rebuilt feed-based shopping campaigns with custom labels by margin tier. AED 320K monthly spend now returns 6.2x ROAS (was 2.1x); ad-attributed revenue up AED 2.4M in 5 months.",
      "Dubai dental implant clinic group — migrated from manual CPC to value-based bidding tied to closed-treatment revenue from HubSpot. Lifetime-value-weighted ROAS now 9.4x; spend scaled from AED 65K to AED 240K/month at improving margin.",
      "MENA logistics B2B SaaS — paired Search with YouTube TrueView retargeting on demo-page visitors. Sales-qualified leads up 218%; cost per closed-won deal fell 41%.",
    ],
  },
  {
    key: "meta-ads",
    name: "Meta Ads",
    tagline: "Creative-led performance scale.",
    proofPoints: ["5.9x ROAS at AED 4M/mo", "+4.2x spend, flat ROAS", "32 UGC variants / month"],
    overview:
      "Creative is 80% of Meta performance. We build a creative-testing engine that ships 30+ new ads per month, finds the winners fast, and scales them aggressively — across Facebook, Instagram, Reels and Stories.",
    deliverables: [
      "Account audit + ad library + competitor teardown",
      "Creative strategy + monthly shotlist (UGC, statics, motion)",
      "30+ ad variants shipped per month",
      "iOS 14.5 / aggregated event measurement setup",
      "Conversion API + Pixel hygiene",
      "Audience + creative testing framework",
      "Weekly creative review + scaling decisions",
    ],
    milestones: [
      { day: "Day 1–10", body: "Audit, account restructure, first creative batch live." },
      { day: "Day 11–45", body: "Winner identified, lookalikes + interest stacks scaled." },
      { day: "Day 46–90", body: "Creative production cadence + scale at 4-6x prior spend." },
    ],
    kpis: ["4-8x ROAS at scale", "+200–600% revenue", "CAC down 30-50%", "Compounding creative library"],
    industries: [
      "Dubai-based DTC fragrance brand at AED 4M/month revenue — set up a creator pod producing 32 UGC variants/month. Identified one hook (\"why your perfume disappears by lunch\") that scaled spend 4.2x at a flat 5.9x ROAS — now contributes 38% of total revenue.",
      "Saudi modest-fashion e-commerce — rebuilt the funnel around catalogue ads + creator-led collection drops. ROAS lifted from 2.1x to 4.6x in 90 days, monthly revenue up AED 1.8M, AOV up 22% via cross-sell collection bundles.",
      "UAE fitness subscription app — replaced founder-led talking-head ads with subtitled member testimonials in 9:16. Sign-up CPA dropped 47%; first-week trial-to-paid conversion improved from 31% to 49%.",
      "Online education platform targeting GCC professionals — engineered a quiz-funnel that pre-qualifies leads before the price reveal. Cost per enrolled student fell from AED 480 to AED 165; 1,840 paid enrolments in Q2.",
      "Premium F&B delivery brand across Dubai — Conversion-API rebuild fixed iOS 14.5 reporting blind spot, exposing 38% of true ROAS that was previously invisible. Reallocated budget to top-performing dayparts; weekend revenue up 71%.",
    ],
  },
  {
    key: "tiktok",
    name: "TikTok Marketing",
    tagline: "Native virality engineering.",
    proofPoints: ["2.4M views in 11 days", "CPM AED 12 (vs AED 84 Meta)", "+612% Shopify sessions"],
    overview:
      "TikTok is its own discipline — what works on Meta does NOT work here. We build native-first creative, ride trending audio + formats, and run TikTok Spark Ads + Lead Gen campaigns calibrated for GCC audiences.",
    deliverables: [
      "Native creator-style content strategy",
      "15–25 TikTok-native videos/month (shot or directed)",
      "Spark Ads + organic content amplification",
      "Trend-jacking response within 24h",
      "Influencer/creator pod-style amplification",
      "Comment + community management",
      "Performance dashboard tracking views → leads",
    ],
    milestones: [
      { day: "Day 1–14", body: "Trend audit, creator briefs, first 10 videos live." },
      { day: "Day 15–45", body: "Hooks tested, Spark Ads turned on for winners." },
      { day: "Day 46–90", body: "Predictable monthly virality + lead capture engine." },
    ],
    kpis: ["1M+ monthly views (organic + paid)", "5-15x lower CPM vs Meta", "+30–80K followers in 90 days", "Direct ROAS 3x+"],
    industries: [
      "UAE modest-wear DTC brand — built a creator pod of 4 GCC voices producing 22 videos/month. One get-ready-with-me format hit 2.4M views in 11 days; follower count grew from 18K to 84K in 90 days; Shopify sessions from TikTok up 612%.",
      "Riyadh-based gaming / esports brand — leveraged trending Saudi gamer audio + behind-the-scenes streamer content. CPM dropped to AED 12 (vs AED 84 on Meta); 380K wishlist sign-ups for new title pre-launch in 8 weeks.",
      "Dubai food delivery startup — Spark Ads on top-performing influencer reviews. AED 9 CPI for app installs (industry average AED 28); 42% week-1 retention vs 18% from Meta.",
      "GCC beauty creator brand — community management arm responded to every comment within 90 minutes during launch window. Drove 38K product-page sessions in the 24h after one viral video; sold out their first SKU within 6 days.",
      "B2C language-learning app targeting MENA youth — used trending UAE/Saudi memes to soften product education. Cost per first-lesson-completed fell from AED 22 to AED 7; subscribers added 14,200 in Q3.",
    ],
  },
  {
    key: "youtube",
    name: "YouTube Marketing",
    tagline: "Long-form authority + ads.",
    proofPoints: ["4K → 89K subs in 6 mo", "11.4-min avg watch time", "AED 14M attributable revenue"],
    overview:
      "YouTube is the highest-LTV channel most agencies ignore. We help founders, coaches and brands build a long-form authority machine — combining a content engine with TrueView, in-stream and Shorts ads.",
    deliverables: [
      "Channel strategy + bilingual content map",
      "4–8 long-form videos/month (concept → edit)",
      "YouTube SEO + thumbnail testing",
      "TrueView + Shorts ad campaigns",
      "Retargeting from view-through audiences",
      "Pre-roll funnel for lead capture",
      "Monthly subscriber + revenue dashboard",
    ],
    milestones: [
      { day: "Day 1–30", body: "Brand voice + content map locked; first 4 videos live." },
      { day: "Day 31–90", body: "Subscriber + watch-time momentum; first ad campaigns optimised." },
      { day: "Day 91+", body: "Compounding views → ad-traffic + organic lead capture." },
    ],
    kpis: ["50K–500K monthly views", "+5–25K subscribers in 90 days", "0.05–0.15 AED cost per view", "Top-of-funnel pipeline 2–3x cheaper than Meta"],
    industries: [
      "UAE founder personal-brand running an 8-figure mastermind — built a content cadence of 2 long-form videos/week with thumbnail A/B testing. Channel grew from 4,200 to 89,000 subscribers in 6 months; high-ticket programme generated AED 14M in attributable revenue.",
      "Dubai PropTech educator — long-form 'how to buy off-plan in Dubai' series turned the channel into the top GCC search result. View-through retargeting closed 41 unit-sales in Q4 with TrueView ad budget under AED 22K/month.",
      "Saudi finance / investing creator — bilingual EN+AR uploads doubled addressable audience. Average watch time 11.4 minutes (industry: 4.8); brand-deal revenue lifted from AED 12K to AED 184K monthly within a year.",
      "MENA B2B SaaS thought-leadership channel — 4 enterprise case-study films per quarter became the top sales-enablement asset. Sales cycle shortened from 84 days to 51 days; demo-show-up rate climbed from 38% to 67%.",
      "Multi-location healthcare consultancy practice — patient-education videos drove 280K monthly views; consultation-page conversions from YouTube view-through retargeting now exceed paid-search.",
    ],
  },
  {
    key: "web",
    name: "Web Development",
    tagline: "High-converting, fast, premium.",
    proofPoints: ["6.2s → 1.1s load time", "+247% form submissions", "90+ Lighthouse guaranteed"],
    overview:
      "We design and build conversion-engineered websites that load under 1.5s, look like Stripe-grade enterprise products, and convert 2–4x better than typical Dubai agency builds. Bilingual EN/AR by default.",
    deliverables: [
      "UX research + conversion-flow design",
      "Custom design system (Figma)",
      "Frontend build (React / Next.js)",
      "CMS integration (Webflow, Sanity, or custom)",
      "Bilingual EN/AR with proper RTL support",
      "GA4 + heatmap + session-recording setup",
      "Speed: 90+ Lighthouse score guaranteed",
    ],
    milestones: [
      { day: "Week 1–2", body: "Discovery, UX wireframes, design system." },
      { day: "Week 3–5", body: "High-fidelity design, copy, content production." },
      { day: "Week 6–8", body: "Build, integration, QA, launch." },
    ],
    kpis: ["2–4x conversion rate vs old site", "≤1.5s load time", "90+ Lighthouse score", "Mobile UX score in top 10%"],
    industries: [
      "Dubai real estate brokerage with 40+ listings — rebuilt on Next.js with a custom map-driven property search. Page load dropped from 6.2s to 1.1s; viewing-request submissions per visitor up 247%; Google ranking for 'Dubai Marina apartments for sale' climbed from page 4 to position 3.",
      "Premium F&B brand with 6 outlets across UAE — built a unified reservation system across all locations with bilingual EN/AR + RTL polish. Online reservations grew from 12% to 41% of total bookings; takeaway revenue via the site added AED 380K/month.",
      "B2B consultancy serving MENA enterprise — replaced their 14-page WordPress site with a 6-page conversion-engineered build. Demo-call bookings doubled; average session duration rose from 47 seconds to 3 minutes 12 seconds.",
      "Wellness clinic chain across 8 UAE locations — built a doctor-discovery UX with filter-by-condition + insurance-network logic. Appointment bookings via the site overtook phone bookings within 90 days.",
      "DIFC-licensed Web3 platform — Stripe-grade design system, sub-1s load, and ENS-style identity flows. Wallet-connection rate per visitor improved from 4.1% to 14.8% — the single highest-ROI change of their year.",
    ],
  },
  {
    key: "branding",
    name: "Branding",
    tagline: "Identity systems that command price.",
    proofPoints: ["AUM threshold AED 5M → 25M", "+28% average per-cover spend", "AED 28M Series A closed"],
    overview:
      "Brand is the single biggest pricing-power lever most founders ignore. We build positioning, visual identity and verbal identity systems that let you charge 30-100% more than competitors — backed by research, not guesswork.",
    deliverables: [
      "Brand strategy workshop (founder + leadership)",
      "Positioning + messaging architecture",
      "Logo, mark, full visual identity",
      "Brand guidelines (50+ page document)",
      "Tone of voice + verbal identity",
      "Photography + iconography system",
      "Launch toolkit (social, presentation, signage)",
    ],
    milestones: [
      { day: "Week 1–2", body: "Strategy workshop + research." },
      { day: "Week 3–5", body: "Identity concepts, refinement, founder approval." },
      { day: "Week 6–8", body: "System build, guidelines, launch toolkit delivered." },
    ],
    kpis: ["+30–100% pricing power", "Higher win-rate in sales conversations", "Investor / partnership doors open"],
    industries: [
      "UAE family-office wealth firm — repositioned from generic 'wealth management' to a category-of-one around inter-generational legacy planning. Minimum AUM threshold raised from AED 5M to AED 25M; closed three AED 100M+ mandates within 6 months of relaunch.",
      "Dubai-based luxury restaurant group — rebuilt the master-brand architecture across 4 sub-brands with shared visual DNA. Average per-cover spend rose 28%; secured a feature in Condé Nast Traveller within the launch quarter.",
      "Saudi premium real estate developer — built a brand system for a flagship AED 3.2 billion project including verbal identity in classical Arabic. 78% of inventory pre-sold during the soft-launch phase against a 42% target.",
      "GCC healthtech startup pre-Series A — refreshed positioning around clinician-first AI rather than generic healthtech. Closed AED 28M Series A within 4 months of rebrand; investor deck cited brand strength as a decision factor.",
      "DIFC fintech preparing for IPO — rebuilt visual + verbal identity for regulator and capital-markets audiences. Equity research analyst coverage doubled within 90 days of the new brand going live.",
    ],
  },
  {
    key: "email",
    name: "Email Marketing",
    tagline: "Lifecycle revenue on autopilot.",
    proofPoints: ["9% → 34% owned-channel revenue", "+22x channel ROI", "AED 480K incremental / mo"],
    overview:
      "Email + SMS routinely drive 30-40% of revenue for the e-commerce brands we work with. We architect lifecycle flows, broadcast calendars, and segmentation that ride alongside paid — so spend efficiency stays high while revenue compounds.",
    deliverables: [
      "Klaviyo / HubSpot / Mailchimp setup + audit",
      "12+ lifecycle flows (welcome, browse abandonment, post-purchase, win-back)",
      "Monthly campaign calendar (4–8 sends/mo)",
      "Bilingual EN/AR templates",
      "Deliverability + sender-reputation management",
      "Segmentation + RFM model",
      "Revenue + LTV reporting",
    ],
    milestones: [
      { day: "Day 1–14", body: "Audit, ESP setup, deliverability cleanup." },
      { day: "Day 15–45", body: "Core flows live; first campaigns shipping." },
      { day: "Day 46–90", body: "30%+ of revenue attributable to owned channels." },
    ],
    kpis: ["30–40% of revenue from owned", "10x ROI on owned channels", "+25% repeat-purchase rate", "LTV up 20-50%"],
    industries: [
      "UAE DTC beauty brand doing AED 12M annual revenue — rebuilt 14 dormant Klaviyo flows and added an RFM-based win-back sequence. Owned-channel revenue jumped from 9% to 34% of total within 4 months; AED 480K incremental monthly revenue at a 22x channel ROI.",
      "Saudi modest-fashion e-commerce — Arabic-first welcome series with right-aligned RTL layouts. Welcome-flow open rates lifted from 18% to 47%; first-purchase rate from new subscribers doubled.",
      "Dubai-based subscription coffee brand — built a churn-prediction flow that triggers a personalised offer 7 days before predicted cancellation. Subscriber churn fell from 14% to 6.8% monthly; LTV up 71%.",
      "Premium home-fragrance brand (GCC + UK) — segmented list by past-purchase scent profile, then ran a curated cross-sell campaign per segment. Cross-sell revenue 4.2x the previous batch-and-blast approach.",
      "UAE-based online education platform — abandoned-checkout flow with installment-plan reminder hooks. Recovered AED 220K in lost revenue per month at near-zero marginal cost.",
    ],
  },
  {
    key: "funnel",
    name: "Funnel Building",
    tagline: "Architected for compounding LTV.",
    proofPoints: ["4.1% → 13.4% booked-call rate", "-58% cost per qualified call", "+340% application volume"],
    overview:
      "We design end-to-end revenue funnels — from cold ad → landing page → lead magnet → email sequence → sales call → upsell — built for compounding LTV, not one-off conversions.",
    deliverables: [
      "Full funnel architecture (mapped on Miro)",
      "Landing page design + build (3–6 pages)",
      "Lead magnets / quizzes / calculators",
      "Email + SMS automation sequences",
      "Booking + payment integration",
      "Upsell + downsell logic",
      "Funnel analytics dashboard",
    ],
    milestones: [
      { day: "Week 1–2", body: "Funnel architecture + offer design." },
      { day: "Week 3–4", body: "Pages + automations built and QA'd." },
      { day: "Week 5+", body: "Live + iterating on conversion data weekly." },
    ],
    kpis: ["2–5x conversion rate", "Cost per booked call 40–60% lower", "LTV +30–80%"],
    industries: [
      "UAE founder selling an AED 35K high-ticket coaching offer — built a quiz-funnel that segments by current revenue range and surfaces a tailored case study before the call invitation. Booked-call rate moved from 4.1% to 13.4%; close-rate on calls rose from 18% to 34%.",
      "MENA management consultancy — replaced 4 standalone service landing pages with a single adaptive funnel keyed off industry + role. Cost per qualified strategy-call dropped 58%; pipeline contribution from organic + paid combined climbed 211%.",
      "Premium Dubai real estate off-plan launch — funnel architecture with an ROI calculator as the lead magnet. Qualified-viewing-bookings rose 220%; conversion to deposit doubled because buyers self-qualified financially before the call.",
      "Online education brand for GCC professionals — VSL → application form → diagnostic call funnel. Application volume up 340% and unqualified-applicant rate dropped from 64% to 19% via funnel-level pre-qualification.",
      "Aesthetics + cosmetic surgery clinic chain — multi-step funnel with treatment-finder quiz and instant in-clinic booking. Cost per consultation booked fell from AED 380 to AED 142; treatment-conversion rate of consultations rose 36%.",
    ],
  },
  {
    key: "ai-chatbots",
    name: "AI Chatbots",
    tagline: "24/7 sales + support agents.",
    proofPoints: ["Speed-to-lead 47h → 38s", "-64% reception workload", "73% tickets auto-resolved"],
    overview:
      "We deploy production-grade AI sales + support agents trained on your knowledge base — handling tier-1 support, qualifying leads, and booking calls 24/7 across web, WhatsApp, and Instagram.",
    deliverables: [
      "AI agent strategy + scope definition",
      "Knowledge-base ingestion + fine-tuning",
      "Web widget + WhatsApp + IG DM integration",
      "Lead qualification + CRM hand-off logic",
      "Multi-language (EN/AR) responses",
      "Escalation to human team logic",
      "Monthly improvement loop based on transcripts",
    ],
    milestones: [
      { day: "Week 1", body: "Scope, KB ingestion, prompt engineering." },
      { day: "Week 2–3", body: "Build, integrate channels, QA." },
      { day: "Week 4+", body: "Live; weekly tuning based on real conversations." },
    ],
    kpis: ["60–80% of inbound auto-resolved", "Support cost down 40-70%", "+25-40% lead-to-call conversion"],
    industries: [
      "UAE multi-location dental group with 28 clinics — built a WhatsApp agent that books appointments, handles insurance pre-checks, and answers common procedure questions in Arabic + English. Reception team workload down 64%; weekend bookings up 184% (clinics were previously closed for phone support).",
      "Dubai real estate brokerage — live-chat agent qualifies budget, area, and timeline before routing hot leads to agents' WhatsApp inside 60 seconds. Speed-to-lead dropped from an industry-average 47 hours to 38 seconds; site-visit bookings up 92%.",
      "DTC e-commerce on Shopify — WhatsApp support agent handles order-status, returns and product Q&A end-to-end. 73% of post-purchase tickets resolved without a human; CSAT score climbed from 4.1 to 4.7.",
      "GCC education platform admissions team — agent qualified 4,200+ applicants in Q2 before routing only top-scoring 18% to human advisors. Advisor-to-enrolment conversion rate doubled; admissions cycle time halved.",
      "B2B SaaS sales team — agent handles product Q&A on the pricing page and books demos on calendar. Tier-1 product-question volume to engineering fell 81%; demo show-up rate improved 22% because the agent confirms calendar fit before scheduling.",
    ],
  },
  {
    key: "crm",
    name: "CRM Automation",
    tagline: "Pipelines that close themselves.",
    overview:
      "We rebuild your sales + lifecycle pipelines on HubSpot, Pipedrive, Zoho or custom stack — automating lead routing, follow-up, deal stage logic, and reporting so your sales team only does what humans must.",
    deliverables: [
      "CRM audit + pipeline redesign",
      "Lead routing + scoring rules",
      "Automated follow-up sequences",
      "Deal-stage automation + reminders",
      "Sales rep dashboards",
      "Integrations (calendar, email, WhatsApp, ads)",
      "Monthly sales-ops review",
    ],
    milestones: [
      { day: "Week 1–2", body: "Audit, blueprint, stakeholder alignment." },
      { day: "Week 3–6", body: "Build, automate, train team." },
      { day: "Week 7+", body: "Adoption + ongoing optimisation." },
    ],
    kpis: ["+30–60% rep productivity", "Lead response time under 5 min", "Deal velocity +20–40%"],
    industries: [
      "Dubai real estate brokerage with 40 agents — rebuilt their HubSpot pipeline with WhatsApp Business integration for instant lead routing. Average first-touch time dropped from 4 hours to 47 seconds; agents' monthly closed-deal count rose from 1.8 to 3.4.",
      "MENA management consultancy — automated proposal follow-up cadence with personalised triggers based on document opens. Proposal-to-signed-contract rate climbed from 28% to 47%; revenue per partner up AED 1.2M annually.",
      "Aesthetic clinic chain across 6 UAE locations — Zoho-based pipeline with treatment-stage automation and reactivation sequences for lapsed patients. Reactivated 380 patients in 90 days, generating AED 1.4M; rep productivity per hour up 38%.",
      "B2B SaaS sales team (Series A) — HubSpot rebuild with lead scoring (model trained on 200 closed deals) routing only top-30% to AEs. Sales-rep time on bad leads fell 71%; closed-won deals per AE per quarter doubled.",
      "Education / EdTech admissions team — automated parent-applicant nurture cadence with milestone reminders (interview, document submission, payment). Drop-off between application and enrolment fell from 38% to 14%.",
    ],
  },
  {
    key: "video",
    name: "Video Editing",
    tagline: "Cinema-grade short & long form.",
    proofPoints: ["3-sec hook retention 38% → 71%", "4.8M cumulative views in mo 1", "CPM -41% vs in-house edits"],
    overview:
      "In-house video team producing scroll-stopping short-form (TikTok, Reels, Shorts) and premium long-form (YouTube, brand films, ads). Native EN + AR. Cinema-grade colour, sound, and motion.",
    deliverables: [
      "Pre-production: concept, script, shotlist",
      "Edit (FCPX / Premiere) with motion graphics",
      "Sound design + colour grading",
      "Captions + bilingual subtitles",
      "Versions for every platform (1:1, 9:16, 16:9)",
      "Thumbnails + cover art",
      "Asset library handover",
    ],
    milestones: [
      { day: "Day 1–3", body: "Concept, script, shotlist locked." },
      { day: "Day 4–10", body: "Edit, motion, sound, grade." },
      { day: "Day 11–14", body: "Reviews, revisions, delivery in all formats." },
    ],
    kpis: ["+200–800% engagement rate", "3-5x hook retention vs in-house edits", "Premium-grade brand perception"],
    industries: [
      "UAE founder personal brand — produced 8 long-form YouTube pieces + 32 short-form cutdowns per month. Average hook retention to 3 seconds climbed from 38% to 71%; one founder-story film drove AED 14M in attributable book sales.",
      "Premium hospitality + F&B group in Dubai — shot and edited the launch film for their flagship venue. 4.8M cumulative views across owned channels in the first month; reservation requests for the location ran at 3x forecast for 8 weeks.",
      "GCC fashion DTC — monthly cadence of 18 Reels-native creative variants. CPM dropped 41% and engagement rate doubled vs the prior in-house edits; one signature transition format became the brand's recurring visual signature.",
      "Real estate developer launching an AED 1.8B off-plan project — cinema-grade pre-launch film with drone + interior CGI hybrid. Used as the centrepiece of the broker roadshow; 64% of inventory reserved during the 6-week launch window.",
      "Tech product launch for a GCC fintech — explainer film + 6 product-feature shorts for paid distribution. Demo-page conversion from video traffic 2.1x higher than from static creative.",
    ],
  },
  {
    key: "content",
    name: "Content Creation",
    tagline: "Story systems, not random posts.",
    proofPoints: ["AED 4.2M inbound LinkedIn pipeline", "240 → 3,800 sign-ups / month", "#1 Google for 17 head terms"],
    overview:
      "We build content systems — not 'social media posts'. A repeatable monthly engine of long-form, short-form, written and visual content that compounds into authority, traffic, and pipeline.",
    deliverables: [
      "Content pillar strategy",
      "Monthly editorial calendar",
      "Long-form (articles, scripts, case studies)",
      "Short-form (Reels, Shorts, carousels)",
      "Photography + on-brand visuals",
      "Distribution playbook",
      "Performance reporting + iteration",
    ],
    milestones: [
      { day: "Week 1", body: "Pillar + calendar locked." },
      { day: "Week 2", body: "First content batch live." },
      { day: "Ongoing", body: "Monthly cadence + performance optimisation." },
    ],
    kpis: ["+40–120% organic engagement", "Authority signals in your niche", "Compounding inbound interest"],
    industries: [
      "B2B consultancy targeting MENA enterprise — partner thought-leadership programme producing 4 long-form pieces + 12 LinkedIn carousels monthly. Inbound qualified pipeline from LinkedIn alone reached AED 4.2M in 7 months; the founding partner is now invited to 3 industry keynotes per quarter.",
      "Coaching / education brand for UAE professionals — 90-day content sprint produced 60 pieces spanning blog, podcast cuts, and short-form. Email-list growth went from 240 sign-ups/month to 3,800; high-ticket programme sold AED 2.1M from organic content alone.",
      "GCC fintech building category authority — produced a 12-piece pillar series on cross-border SME finance. The series ranks #1 in Google for 17 head terms and drives 38% of demo bookings inbound.",
      "Healthcare consultancy practice — patient-education content engine across blog + Instagram + Reels. Consultations booked via organic content rose 184%; patient acquisition cost dropped to 28% of paid equivalent.",
      "Premium Dubai real estate developer — buyer-journey content covering legal, financing, and resale considerations. Average time-on-site for organic visitors climbed to 6 minutes 12 seconds; off-plan enquiries from organic content up 213%.",
    ],
  },
  {
    key: "influencer",
    name: "Influencer Marketing",
    tagline: "Vetted creators, measured ROI.",
    proofPoints: ["6.2x ROAS on launch", "+3x branded search 14 days", "Capsule sold out in 9 days"],
    overview:
      "We run influencer campaigns the way performance marketers run paid ads — vetted creator pools, structured briefs, tracked ROI. No guesswork, no 'follower counts' as a KPI.",
    deliverables: [
      "Creator strategy + tier mix (mega/macro/micro/nano)",
      "Database of 500+ vetted GCC creators",
      "Creator outreach + negotiation",
      "Brief writing + content approval",
      "Tracking (UTMs, codes, dedicated landers)",
      "Whitelisting + ad amplification",
      "Campaign reporting with ROI attribution",
    ],
    milestones: [
      { day: "Week 1–2", body: "Strategy + creator outreach." },
      { day: "Week 3–5", body: "Content production + approval." },
      { day: "Week 6+", body: "Go-live + tracked performance." },
    ],
    kpis: ["3–8x ROAS on campaign spend", "+40–150% reach lift", "Compounding brand-search lift"],
    industries: [
      "DTC beauty brand across UAE + Saudi — orchestrated a 24-creator launch campaign with whitelisted Spark Ads on top performers. Campaign ROAS hit 6.2x; brand-search volume on Google tripled within 14 days of go-live.",
      "Fashion + modest-wear DTC — mix of 4 macro creators + 18 micro creators for a Ramadan capsule drop. Capsule sold out in 9 days against a 30-day forecast; reached 4.8M unique people across the GCC.",
      "Fitness app + supplements crossover — performance-style codes per creator with per-creator landing pages. Cost per app install via influencer 41% lower than Meta-only; supplement attach rate from app installs hit 28%.",
      "Restaurant + café opening in Dubai — 12 lifestyle creators across opening week with a tracked reservation code. The venue ran a 6-week waitlist; AED 720K F&B revenue attributable to the influencer launch in month one.",
      "Premium F&B + beverage launch — flagship campaign with 3 macro voices + 30 nano creators. Generated 28K UGC-style assets the brand now uses in paid media; CPM on whitelisted ads dropped 56% vs branded creative.",
    ],
  },
  {
    key: "marketplace",
    name: "Marketplace Marketing",
    tagline: "Amazon, Noon, Flipkart, Etsy scale.",
    proofPoints: ["AED 180K → 720K / mo Amazon", "#2 BSR 'Arabian perfumes' in 11 wks", "Reviews 240 → 1,840 in 90 days"],
    overview:
      "We grow brands on Amazon (AE / SA / global), Noon, Flipkart, and Etsy — listings optimisation, PPC, A+ content, brand store builds, and inventory + ranking strategy.",
    deliverables: [
      "Marketplace audit + listing optimisation",
      "Keyword + competitor research",
      "A+ / Premium A+ content + brand store",
      "PPC campaign management (Sponsored Products + Brands + Display)",
      "Review + ratings management",
      "Inventory + ranking strategy",
      "Multi-platform expansion (AE → SA → UK)",
    ],
    milestones: [
      { day: "Week 1–2", body: "Audit, listing rewrites, PPC restructure." },
      { day: "Week 3–6", body: "A+ live, PPC scaled, BSR climbing." },
      { day: "Month 3+", body: "Category-leading rank + multi-platform expansion." },
    ],
    kpis: ["+150–400% revenue", "TACoS down 30–50%", "Top-3 BSR in category"],
    industries: [
      "UAE home & living DTC selling on Amazon AE — rewrote 84 listings with conversion-led copy + Premium A+ modules. Conversion rate on top SKUs lifted from 7.4% to 14.1%; Amazon revenue grew from AED 180K to AED 720K/month in 5 months.",
      "Saudi-based oud + perfume brand — launched on Amazon SA + Noon simultaneously with a coordinated PPC playbook. Took the #2 BSR rank in 'Arabian perfumes' within 11 weeks; TACoS settled at 14% (industry: 28%).",
      "Health supplements brand (GCC) — fixed review-velocity issues via a compliant insert programme and ran Sponsored Display retargeting. Review count grew from 240 to 1,840 in 90 days; subscribe-and-save penetration reached 22%.",
      "Mother + baby DTC — built bundle SKUs to lift AOV from AED 92 to AED 184. Then PPC-scaled the bundles aggressively; Amazon revenue ran ahead of D2C site revenue within Q2.",
      "Tech accessories brand expanding across AE → SA → UK — coordinated Vine programmes, Premium A+ stores, and inventory forecasts. Multi-marketplace revenue crossed AED 1.4M/month within 9 months of launch.",
    ],
  },
  {
    key: "saas",
    name: "SaaS Growth Strategy",
    tagline: "PLG, paid, retention — full stack.",
    proofPoints: ["ARR AED 4.8M → 11.2M in 9 mo", "Net retention 96% → 118%", "Sales cycle 86 → 54 days"],
    overview:
      "We work as your fractional growth team — running paid acquisition, lifecycle, PLG experimentation, retention, and revenue ops. Aligned to ARR + net retention, not vanity metrics.",
    deliverables: [
      "Growth audit + 90-day blueprint",
      "Paid acquisition (Google, LinkedIn, Meta)",
      "Lifecycle email + product-led nudges",
      "Onboarding optimisation",
      "Retention + expansion experiments",
      "Revenue + CAC dashboards",
      "Weekly leadership sync",
    ],
    milestones: [
      { day: "Day 1–14", body: "Audit + blueprint signed off." },
      { day: "Day 15–60", body: "First experiments live (paid + lifecycle)." },
      { day: "Day 60+", body: "Compounding ARR growth + net-retention lift." },
    ],
    kpis: ["+50–200% pipeline", "CAC down 30–50%", "Net retention 110%+"],
    industries: [
      "MENA HRTech at Series A — rebuilt onboarding around an aha-moment activation event (first payroll run). Activation rate climbed from 38% to 64%; trial-to-paid conversion doubled; ARR grew from AED 4.8M to AED 11.2M in 9 months.",
      "GCC fintech B2B (DIFC-licensed) — ran an ABM motion targeting the top-80 regional treasurer roles. Booked 24 enterprise demos in Q3; 6 closed at average ACV AED 380K, lifting MRR by AED 190K.",
      "Vertical SaaS for clinics + healthcare — added an expansion-revenue motion via per-location upgrades. Net revenue retention rose from 96% to 118%; existing-customer revenue contribution climbed from 22% to 41% of growth.",
      "Logistics + supply-chain SaaS targeting GCC fleet operators — paired programmatic SEO with a value-based Google Ads layer. Pipeline up 238% in 9 months; sales cycle shortened from 86 days to 54 days.",
      "Education / LMS platform — PLG free-tier with paid-team upsell triggered by team-size threshold. Self-serve revenue grew from AED 120K to AED 480K MRR; sales-assisted PLG closed an additional AED 280K MRR per quarter.",
    ],
  },
];

export default SERVICES;
 SERVICES;
